import styled from "@emotion/styled";
import { Chip, IconButton, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Pencil } from "@phosphor-icons/react";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useApproversPaginated,
  useFinanceSettingsStore
} from "features/finances";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const ApproverTable = ({ onEdit, onboarding }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const openCreateApproverModal = useFinanceSettingsStore(
    state => state.openCreateApproverModal
  );
  const openUpdateApproverModal = useFinanceSettingsStore(
    state => state.openUpdateApproverModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const approversQuery = useApproversPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize
  });

  const columns = [
    {
      field: "approverName",
      headerName: "Approver Name",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <span>
            {row.approver.first_name} {row.approver.last_name}{" "}
            <Chip
              label={row.approver_status}
              color={row.approver_status === "ACTIVE" ? "success" : "error"}
              size="small"
            />
          </span>
        );
      }
    },
    {
      field: "approver_level",
      headerName: "Approval Level",
      flex: 1
    },
    {
      field: "approval_limit_per_request",
      headerName: "Max per Request",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "approval_limit_per_month",
      headerName: "Max per Month",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      hide: onboarding,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row">
            <IconButton
              onClick={() => {
                onEdit(row);
                openUpdateApproverModal();
              }}
            >
              <Pencil />
            </IconButton>
          </Stack>
        );
      }
    }
  ];

  return (
    <Container>
      {!onboarding && (
        <Stack alignItems="flex-start" spacing={4}>
          <Header>
            <Title>Approver List</Title>
            <PrimaryActions>
              <Button onClick={openCreateApproverModal}>Create Approver</Button>
            </PrimaryActions>
          </Header>
        </Stack>
      )}
      <DataGrid
        autoHeight
        rows={approversQuery.data?.result?.data || []}
        rowCount={approversQuery.data?.result?.total || 0}
        loading={approversQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </Container>
  );
};
